/* eslint-disable */
import { useState } from "react";
import {
  Car, UserRound, Building2, Coffee, Handshake, ShieldCheck, Wrench, MapPin, KeyRound,
} from "lucide-react";
import { TEAL, WHITE } from "./components";
import { useLanguage, LANG_HANT, LANG_HANS } from "./i18n/LanguageContext";

/**
 * Comma-separated in `.env.local`: REACT_APP_INVITE_CODES=CODE1,CODE2
 * If unset (e.g. hosting build without env), falls back to default `8go` so the gate always works.
 * Set REACT_APP_DISABLE_INVITE_GATE=1 to turn off the invite requirement entirely.
 */
const INVITE_STORAGE_KEY = "8go-invite-unlocked";
const INVITE_DISABLED = process.env.REACT_APP_DISABLE_INVITE_GATE === "1";
const fromEnv = (process.env.REACT_APP_INVITE_CODES || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);
const VALID_INVITE_CODES = INVITE_DISABLED
  ? []
  : fromEnv.length > 0
    ? fromEnv
    : ["8go"];
const INVITE_GATE_ON = VALID_INVITE_CODES.length > 0;

const LOGO_HERO = `${process.env.PUBLIC_URL}/logo-hero.png`;

const fontStack = "'Noto Sans TC','Plus Jakarta Sans',system-ui,sans-serif";
const fontLatin = "'Plus Jakarta Sans','Noto Sans TC',system-ui,sans-serif";

const glass = {
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
};

const btnPrimary = {
  background: "linear-gradient(180deg,rgba(255,255,255,0.98) 0%,rgba(240,253,250,0.95) 100%)",
  color: "#0f766e",
  border: "1px solid rgba(255,255,255,0.5)",
  borderRadius: 20,
  padding: "18px 16px",
  minHeight: 56,
  fontSize: 20,
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
  lineHeight: 1.3,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  fontFamily: fontStack,
};

const btnGhost = {
  ...glass,
  background: "rgba(255,255,255,0.07)",
  color: WHITE,
  border: "1px solid rgba(255,255,255,0.28)",
  borderRadius: 20,
  padding: "18px 16px",
  minHeight: 56,
  fontSize: 20,
  fontWeight: 700,
  cursor: "pointer",
  lineHeight: 1.3,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
  fontFamily: fontStack,
};

const btnSoft = {
  ...glass,
  background: "rgba(255,255,255,0.05)",
  color: "rgba(255,255,255,0.95)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 20,
  padding: "16px 16px",
  minHeight: 52,
  fontSize: 18,
  fontWeight: 600,
  cursor: "pointer",
  lineHeight: 1.3,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  fontFamily: fontStack,
};

/** 圖片載入失敗時嘅簡化公路 8 字標 */
const FallbackLogo = () => (
  <svg viewBox="0 0 140 140" width={128} height={128} aria-hidden style={{
    filter: "drop-shadow(0 0 28px rgba(45,212,191,0.45))",
  }}>
    <defs>
      <linearGradient id="goMetal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f1f5f9" />
        <stop offset="45%" stopColor="#94a3b8" />
        <stop offset="100%" stopColor="#475569" />
      </linearGradient>
    </defs>
    <ellipse cx={70} cy={48} rx={28} ry={22} fill="none" stroke="url(#goMetal)" strokeWidth={8} />
    <ellipse cx={70} cy={92} rx={28} ry={22} fill="none" stroke="url(#goMetal)" strokeWidth={8} />
    <line x1={52} y1={48} x2={88} y2={48} stroke="rgba(255,255,255,0.85)" strokeWidth={2} strokeDasharray="5 6" />
    <line x1={52} y1={92} x2={88} y2={92} stroke="rgba(255,255,255,0.85)" strokeWidth={2} strokeDasharray="5 6" />
  </svg>
);

const Home = ({ go }) => {
  const { lang, setLang, t } = useLanguage();
  const [logoHeroOk, setLogoHeroOk] = useState(true);
  const [inviteUnlocked, setInviteUnlocked] = useState(() => {
    if (!INVITE_GATE_ON) return true;
    try {
      return localStorage.getItem(INVITE_STORAGE_KEY) === "1";
    } catch (e) {
      return false;
    }
  });
  const [inviteInput, setInviteInput] = useState("");
  const [inviteError, setInviteError] = useState("");

  const tryUnlockInvite = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const v = inviteInput.trim();
    if (VALID_INVITE_CODES.includes(v)) {
      try {
        localStorage.setItem(INVITE_STORAGE_KEY, "1");
      } catch (err) { /* ignore */ }
      setInviteUnlocked(true);
      setInviteError("");
      setInviteInput("");
    } else {
      setInviteError(t("home.inviteWrong"));
    }
  };

  const mainLocked = INVITE_GATE_ON && !inviteUnlocked;
  const langPill = (active) => ({
    padding: "10px 20px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 700,
    border: active ? "1px solid rgba(255,255,255,0.55)" : "1px solid rgba(255,255,255,0.2)",
    background: active ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
    color: WHITE,
    ...glass,
    fontFamily: fontStack,
  });

  const cityPill = {
    ...glass,
    background: "rgba(255,255,255,0.06)",
    color: WHITE,
    borderRadius: 999,
    padding: "10px 18px",
    fontSize: 15,
    fontWeight: 600,
    border: "1px solid rgba(255,255,255,0.22)",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
    fontFamily: fontStack,
  };

  return (
    <div
      className="go-fade"
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "36px 22px 48px",
        boxSizing: "border-box",
        background: "linear-gradient(165deg,#020f0d 0%,#052e2a 28%,#0a4d45 55%,#063530 100%)",
        fontFamily: fontStack,
      }}
    >
      {/* 底圖：淡街網 + 中央光暈 */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(ellipse 85% 55% at 50% 22%, rgba(45,212,191,0.14) 0%, transparent 52%),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)
          `,
          backgroundSize: "auto, 56px 56px, 56px 56px",
          opacity: 0.45,
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "-20% -30% 40%",
          pointerEvents: "none",
          background: "radial-gradient(ellipse at 50% 0%, rgba(20,184,166,0.18) 0%, transparent 55%)",
        }}
      />
      {/* 透明 PNG logo：頂部輕微青光即可 */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: 0,
          transform: "translateX(-50%)",
          width: "min(100vw, 480px)",
          height: "min(50vh, 360px)",
          pointerEvents: "none",
          background: "radial-gradient(ellipse 80% 70% at 50% 25%, rgba(45,212,191,0.12) 0%, transparent 65%)",
        }}
      />

      <div style={{ position: "relative", zIndex: 1, textAlign: "center", marginBottom: 36, maxWidth: 440, width: "100%" }}>
        {logoHeroOk ? (
          <>
            <figure
              style={{
                margin: "0 auto",
                padding: "12px 16px 8px",
                maxWidth: 360,
                width: "100%",
                background: "transparent",
              }}
            >
              <img
                src={LOGO_HERO}
                alt={t("home.tagline")}
                width={800}
                height={800}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  verticalAlign: "top",
                  objectFit: "contain",
                  objectPosition: "center center",
                  border: "none",
                  boxShadow: "none",
                  filter: "drop-shadow(0 16px 32px rgba(2,12,11,0.5)) drop-shadow(0 0 20px rgba(45,212,191,0.12))",
                }}
                onError={() => setLogoHeroOk(false)}
              />
            </figure>
            <div
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.78)",
                marginTop: 4,
                letterSpacing: 0.4,
                lineHeight: 1.55,
                maxWidth: 380,
                marginLeft: "auto",
                marginRight: "auto",
                fontFamily: fontStack,
              }}
            >
              {t("home.tagline")}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22, flexWrap: "wrap" }}>
              {["大多倫多", "溫哥華", "卡加利"].map((c) => (
                <span key={c} style={cityPill}>
                  <MapPin size={17} color={WHITE} strokeWidth={2.4} aria-hidden />
                  {c}
                </span>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 18, display: "flex", justifyContent: "center" }}>
              <FallbackLogo />
            </div>
            <div
              style={{
                fontSize: "clamp(2rem, 7vw, 2.75rem)",
                fontWeight: 900,
                color: "#fff",
                letterSpacing: 3,
                lineHeight: 1.15,
                textShadow: "0 0 42px rgba(45,212,191,0.35), 0 4px 24px rgba(0,0,0,0.45)",
                fontFamily: fontStack,
              }}
            >
              八達<span style={{ fontFamily: fontLatin, letterSpacing: 2 }}>Go</span>
            </div>
            <div
              style={{
                fontSize: 16,
                color: "rgba(255,255,255,0.78)",
                marginTop: 12,
                letterSpacing: 0.4,
                lineHeight: 1.55,
                maxWidth: 380,
                marginLeft: "auto",
                marginRight: "auto",
                fontFamily: fontStack,
              }}
            >
              {t("home.tagline")}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 22, flexWrap: "wrap" }}>
              {["大多倫多", "溫哥華", "卡加利"].map((c) => (
                <span key={c} style={cityPill}>
                  <MapPin size={17} color={WHITE} strokeWidth={2.4} aria-hidden />
                  {c}
                </span>
              ))}
            </div>
          </>
        )}
        <p style={{
          margin: "26px 0 10px",
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 1.2,
          color: "rgba(255,255,255,0.45)",
          textTransform: "uppercase",
          fontFamily: fontLatin,
        }}>
          Language · 介面語言
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button type="button" onClick={() => setLang(LANG_HANT)} style={langPill(lang === LANG_HANT)}>
            {t("lang.hant")}
          </button>
          <button type="button" onClick={() => setLang(LANG_HANS)} style={langPill(lang === LANG_HANS)}>
            {t("lang.hans")}
          </button>
        </div>
      </div>

      {INVITE_GATE_ON && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 400,
            marginBottom: 18,
            padding: 20,
            borderRadius: 22,
            ...glass,
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.22)",
            boxSizing: "border-box",
            boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: WHITE, fontSize: 16, fontWeight: 700 }}>
            <KeyRound size={20} color={WHITE} strokeWidth={2.2} aria-hidden />
            {t("home.inviteLabel")}
          </div>
          {inviteUnlocked ? (
            <p style={{ margin: 0, fontSize: 15, color: "#99f6e4", fontWeight: 600, lineHeight: 1.5 }}>
              ✓ {t("home.inviteOk")}
            </p>
          ) : (
            <form onSubmit={tryUnlockInvite} style={{ margin: 0 }}>
              <p style={{ margin: "0 0 12px", fontSize: 14, color: "rgba(255,255,255,0.88)", lineHeight: 1.45 }}>
                {t("home.inviteHint")}
              </p>
              <input
                type="text"
                name="inviteCode"
                autoComplete="off"
                value={inviteInput}
                onChange={(ev) => { setInviteInput(ev.target.value); setInviteError(""); }}
                placeholder={t("home.invitePlaceholder")}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "14px 16px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: "rgba(15,40,36,0.65)",
                  color: "#ecfdf5",
                  fontSize: 17,
                  fontWeight: 600,
                  marginBottom: 10,
                  fontFamily: fontStack,
                }}
              />
              {inviteError ? (
                <div style={{ fontSize: 14, color: "#fecaca", marginBottom: 10, fontWeight: 600 }} role="alert">{inviteError}</div>
              ) : null}
              <button type="submit" style={{ ...btnPrimary, width: "100%", marginTop: 4 }}>
                <KeyRound size={22} color={TEAL} strokeWidth={2.25} aria-hidden />
                {t("home.inviteSubmit")}
              </button>
            </form>
          )}
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 18 }}>
        {mainLocked ? (
          <div style={{ textAlign: "center", fontSize: 15, color: "rgba(255,255,255,0.82)", padding: "8px 12px", lineHeight: 1.5 }}>
            {t("home.inviteLocked")}
          </div>
        ) : null}
        <button
          type="button"
          disabled={mainLocked}
          onClick={() => go("passenger")}
          style={{
            ...btnPrimary,
            opacity: mainLocked ? 0.45 : 1,
            cursor: mainLocked ? "not-allowed" : "pointer",
            pointerEvents: mainLocked ? "none" : "auto",
          }}
        >
          <UserRound size={26} color={TEAL} strokeWidth={2.25} aria-hidden />
          {t("home.findRide")}
        </button>
        <button
          type="button"
          disabled={mainLocked}
          onClick={() => go("driver")}
          style={{
            ...btnGhost,
            opacity: mainLocked ? 0.45 : 1,
            cursor: mainLocked ? "not-allowed" : "pointer",
            pointerEvents: mainLocked ? "none" : "auto",
          }}
        >
          <Car size={26} color={WHITE} strokeWidth={2.25} aria-hidden />
          {t("home.driver")}
        </button>
        <button
          type="button"
          disabled={mainLocked}
          onClick={() => go("community")}
          style={{
            ...btnSoft,
            opacity: mainLocked ? 0.45 : 1,
            cursor: mainLocked ? "not-allowed" : "pointer",
            pointerEvents: mainLocked ? "none" : "auto",
          }}
        >
          <Building2 size={24} color={WHITE} strokeWidth={2.2} aria-hidden />
          {t("home.community")}
        </button>
      </div>

      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: 28,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 12,
          maxWidth: 400,
          fontSize: 15,
          color: "rgba(255,255,255,0.82)",
          lineHeight: 1.5,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Coffee size={17} strokeWidth={2.4} color="rgba(255,255,255,0.92)" aria-hidden />
          {t("home.coffee")}
        </span>
        <span style={{ opacity: 0.45 }}>·</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Handshake size={17} strokeWidth={2.4} color="rgba(255,255,255,0.92)" aria-hidden />
          {t("home.match")}
        </span>
        <span style={{ opacity: 0.45 }}>·</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <ShieldCheck size={17} strokeWidth={2.4} color="rgba(255,255,255,0.92)" aria-hidden />
          {t("home.verify")}
        </span>
      </div>

      <button
        type="button"
        onClick={() => go("admin")}
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: 22,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontSize: 14,
          color: "rgba(255,255,255,0.45)",
          padding: "12px 16px",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: fontStack,
        }}
      >
        <Wrench size={16} strokeWidth={2.3} color="rgba(255,255,255,0.5)" aria-hidden />
        {t("home.admin")}
      </button>
    </div>
  );
};

export default Home;
